import React, { Component } from 'react'
import Runner from '@protonapp/proton-runner'
import qs from 'qs'
import axios from 'axios'
import DocumentTitle from 'react-document-title'
import SafeAreaInsets from 'safe-area-insets'
import StatusBar from '@protonapp/react-status-bar'
import Helmet from 'react-helmet'

import {
  DATABASE_URL,
  DATABASE_API_URL,
  ASSETS_URL,
  FILE_UPLOADS_URL,
  IMAGE_UPLOADS_URL,
  NOTIFICATIONS_URL,
} from '../config'

import { getAppURL, getAppIdURL } from '../utils/io'
import { getLibraries } from '../utils/libraries'
import InstallPrompt from './InstallPrompt'
import LibraryLoader from './LibraryLoader'
import IOSInstallPrompt from './IOSInstallPrompt'

import '@protonapp/react-status-bar/styles.css'
import './Renderer.css'

const isMobile = window === window.top

export default class Renderer extends Component {
  state = {
    app: null,
    authenticationError: false,
    currentComponentId: null,
    installPrompt: false,
    librariesReady: false,
    error: false,
    appId: null,
  }

  requestApp = async () => {
    let { match } = this.props
    let { appId } = match.params

    const authToken = process.env.REACT_APP_BACKEND_SERVICE

    let url

    if (appId) {
      url = getAppURL(appId)
    } else {
      try {
        let host = window.location.host.split(':')[0]
        let query = `host=${encodeURIComponent(host)}`
        const appIdURL = getAppIdURL(query)
        const { data } = await axios.get(appIdURL)
        url = getAppURL(data.id)
      } catch (error) {
        this.setState({ error: true })
        console.error(`ERROR RESOLVING APP HOST:  ${error}`)
      }
    }

    try {
      axios.defaults.headers['x-server-auth'] = authToken
      let response = await axios.get(url)
      this.setState({ app: response.data, appId: response.data.id })
    } catch (error) {
      this.setState({ error: true })
      console.info(`ERROR GETTING APP: ${error}`)
    }
  }

  handleLoadLibraries = () => {
    this.setState({ librariesReady: true })
  }

  handleUnauthorized = () => {
    this.setState({ authenticationError: true })
  }

  handleRouteChange = newRoute => {
    this.setState({ currentComponentId: newRoute.componentId })
  }

  getParams = () => {
    return qs.parse(window.location.search.substring(1))
  }

  getOS = () => {
    return this.getParams().os
  }

  getLayoutGuides = () => {
    let params = this.getParams()

    if (SafeAreaInsets.top > 0) {
      return {
        top: SafeAreaInsets.top,
        bottom: 0,
      }
    }

    return {
      top: +params.offset_top || 0,
      bottom: +params.offset_bottom || 0,
    }
  }

  showInstallPrompt = () => {
    this.setState({ installPrompt: true })
  }

  beforeInstallPrompt = installPrompt => {
    this.setState({ installPrompt })
  }

  componentDidMount() {
    this.requestApp()

    window.addEventListener('beforeinstallprompt', this.beforeInstallPrompt)
  }

  renderStatusBar() {
    let { app, currentComponentId } = this.state

    if (isMobile) {
      return null
    }

    if (!app || !app.components) {
      return null
    }

    let component = app.components[currentComponentId]
    let layoutGuides = this.getLayoutGuides()

    if (!component) {
      return null
    }

    let { statusBarStyle } = component
    let light = false
    let hasNotch = layoutGuides.top > 20

    if (statusBarStyle === 'hidden') {
      return null
    }

    if (statusBarStyle === 'light') {
      light = true
    }

    return (
      <div className="status-bar-wrapper">
        <StatusBar platform={this.getOS()} light={light} hasNotch={hasNotch} />
      </div>
    )
  }

  render() {
    let {
      app,
      librariesReady,
      authenticationError,
      installPrompt,
      error,
      appId,
    } = this.state

    let { offsetTop, analytics } = this.props

    let showIOSInstallPrompt = true

    let disableAnalytics = !analytics

    if (authenticationError) {
      return (
        <div>
          <h1>Unauthorized</h1>
        </div>
      )
    } else if (error) {
      return (
        <div>
          <h1>Error getting app</h1>
        </div>
      )
    }

    let layoutGuides = this.getLayoutGuides()
    let libraries = getLibraries(app)

    return (
      <React.Fragment>
        <Helmet>
          <link
            rel="shortcut icon"
            href={`${getAppURL(appId)}/icon?size=180&radius=7`}
          />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href={`${getAppURL(appId)}/icon?size=180&radius=7`}
          />
        </Helmet>
        {app ? <DocumentTitle title={app.name} /> : null}
        <LibraryLoader app={app} onLoad={this.handleLoadLibraries} />
        <Runner
          app={librariesReady ? app : null}
          baseURL={DATABASE_URL}
          baseAPIURL={DATABASE_API_URL}
          assetsBaseURL={ASSETS_URL}
          fileUploadsBaseURL={FILE_UPLOADS_URL}
          imageUploadsBaseURL={IMAGE_UPLOADS_URL}
          notificationsURL={NOTIFICATIONS_URL}
          transitionStyle={this.getOS()}
          onRoute={this.handleRouteChange}
          layoutGuides={layoutGuides}
          libraries={librariesReady && libraries}
          offsetTop={offsetTop}
          disableAnalytics={disableAnalytics}
        />
        {app ? this.renderStatusBar() : null}
        {app && installPrompt ? (
          <InstallPrompt app={app} onShow={installPrompt} />
        ) : null}
        {showIOSInstallPrompt ? <IOSInstallPrompt app={app} /> : null}
      </React.Fragment>
    )
  }
}
